import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Pipeline, REQ_LABEL, Stage } from '../api';
import { ErrorBox, useAsync } from '../components/ui';

type Row = Omit<Stage, 'id' | 'position'> & { id?: string; key: string };
const KIND: Record<string, string> = { open: 'nyitott', won: 'megnyert', lost: 'elvesztett' };
let seq = 0;

/** Pipeline-ok és lépéseik szerkesztése (tulajdonos / adminisztrátor). */
export function PipelineSettingsPage() {
  const [pipes, setPipes] = useState<Pipeline[]>([]);
  const [sel, setSel] = useState<string>('');
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [saved, setSaved] = useState(false);
  const a = useAsync();
  const load = (keep?: string) => a.run(async () => {
    const r = await api<{ items: Pipeline[] }>('/pipelines');
    setPipes(r.items);
    pick(r.items.find((p) => p.id === keep) ?? r.items[0]);
  });
  const pick = (p?: Pipeline) => {
    setSaved(false);
    if (!p) { setSel('new'); setName('Új pipeline'); setRows([
      { key: `n${seq++}`, name: 'Első lépés', probability: 20, kind: 'open', required_fields: [], stale_days: 14 },
      { key: `n${seq++}`, name: 'Megnyert', probability: 100, kind: 'won', required_fields: [], stale_days: null },
      { key: `n${seq++}`, name: 'Elvesztett', probability: 0, kind: 'lost', required_fields: [], stale_days: null }]); return; }
    setSel(p.id); setName(p.name); setRows(p.stages.map((s) => ({ ...s, key: s.id })));
  };
  useEffect(() => { load(); }, []);

  const upd = (i: number, patch: Partial<Row>) => { setSaved(false); setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r))); };
  const swap = (i: number, j: number) => { if (j < 0 || j >= rows.length) return; const c = [...rows]; [c[i], c[j]] = [c[j], c[i]]; setRows(c); setSaved(false); };
  const save = async () => {
    const body = { name, stages: rows.map(({ key, ...r }) => ({ ...r, stale_days: r.kind === 'open' ? r.stale_days : null })) };
    const p = await a.run(() => api<Pipeline>(sel === 'new' ? '/pipelines' : `/pipelines/${sel}`, { method: sel === 'new' ? 'POST' : 'PUT', body }));
    if (p) { await load(p.id); setSaved(true); }
  };

  return (
    <div>
      <div className="page-head">
        <h2>Értékesítési lépések</h2>
        <select aria-label="Pipeline" value={sel} onChange={(e) => pick(pipes.find((p) => p.id === e.target.value))} style={{ width: 'auto' }}>
          {pipes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          <option value="new">+ új pipeline</option>
        </select>
        <div className="spacer" />
        <Link to="/ertekesites">← Tábla</Link>
      </div>
      <section className="card">
        <label className="field"><span>Pipeline neve</span><input value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} maxLength={100} /></label>
        <table className="list stages">
          <thead><tr><th></th><th>Lépés</th><th>Típus</th><th className="num">Valószínűség %</th><th className="num">Elakadt, ha (nap)</th><th>Kötelező mezők</th><th></th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key}>
                <td className="nowrap"><button className="link" onClick={() => swap(i, i - 1)} aria-label="Fel">↑</button> <button className="link" onClick={() => swap(i, i + 1)} aria-label="Le">↓</button></td>
                <td><input value={r.name} onChange={(e) => upd(i, { name: e.target.value })} maxLength={100} /></td>
                <td><select value={r.kind} onChange={(e) => upd(i, { kind: e.target.value as Row['kind'] })}>{Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                <td className="num"><input type="number" min={0} max={100} value={r.probability} onChange={(e) => upd(i, { probability: Number(e.target.value) })} style={{ width: 80 }} /></td>
                <td className="num">{r.kind === 'open'
                  ? <input type="number" min={1} max={365} value={r.stale_days ?? ''} placeholder="–" onChange={(e) => upd(i, { stale_days: e.target.value ? Number(e.target.value) : null })} style={{ width: 80 }} />
                  : <span className="muted">–</span>}</td>
                <td>{Object.entries(REQ_LABEL).map(([f, l]) => (
                  <label key={f} className="check small"><input type="checkbox" checked={r.required_fields.includes(f)}
                    onChange={(e) => upd(i, { required_fields: e.target.checked ? [...r.required_fields, f] : r.required_fields.filter((x) => x !== f) })} /> {l}</label>
                ))}</td>
                <td><button className="link" onClick={() => { setRows(rows.filter((_, k) => k !== i)); setSaved(false); }} aria-label="Törlés">törlés</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="actions">
          <button className="secondary" onClick={() => { setRows([...rows.slice(0, -2), { key: `n${seq++}`, name: '', probability: 50, kind: 'open', required_fields: [], stale_days: 14 }, ...rows.slice(-2)]); setSaved(false); }}>+ Lépés</button>
          <button onClick={save} disabled={a.busy || !name.trim() || rows.some((r) => !r.name.trim())}>Mentés</button>
          {saved && <span className="muted">Elmentve.</span>}
        </div>
        <p className="muted small">Pontosan egy „megnyert” és egy „elvesztett” lépés kell. Olyan lépés nem törölhető, amelyben deal van.</p>
        <ErrorBox error={a.error} />
      </section>
    </div>
  );
}
